import { useState } from "react";
import { Loader2 } from "lucide-react";
import { DateRangePicker } from "../../components/DateRangePicker";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Icon } from "../../components/msd/Icon";
import { CARD, Empty, LBL, LinkBtn, Pill, SECL, SelectBox, Tag, cx } from "../../components/msd/ui";
import { useAuth } from "../../lib/AuthContext";
import { isWaGroupDeleted } from "../../lib/journey";
import VehicleThumb from "@packs/automotive/web/pages/ServiceDesk/VehicleThumb";
import { useGroupMessageLog, useStakeholders, useMemberChanges, useGroupEvents } from "../../lib/api";
import { presentNumbers, awaitingJoinFromEvents, customerInGroup } from "../../lib/members";
import {
  escalationSnippet,
  currentUserNotJoined,
  serviceAdvisorNotJoined,
  customerNotJoined,
  customerNotJoinedForCard,
  isGroupCreating,
  pendingMemberCountForCard,
} from "./deskBuckets";
import { buildMembers } from "./useDeskSummary";

// DateRangePicker has no per-day activity to show here (unlike Workshop
// Analytics' heat-dot calendar) -- a fixed empty map keeps its identity
// stable across renders rather than recreating one every time.
const NO_DATE_ACTIVITY = new Map();

// The design's icon and colour for each queue; a queue it doesn't name keeps
// the icon it was defined with.
const FILTER_ICON = {
  pending_invites: ["userplus", "#F79009"],
  customer_not_joined: ["userx", "#F79009"],
  unanswered: ["chat", "#3A5F94"],
  job_card_opened: ["doc", "#3A5F94"],
  job_card_closed: ["circlecheck", "#17B26A"],
  escalations: ["warn", "#F04438"],
};

// The WhatsApp number and advisor filters, side by side when both show.
const SELECT = "h-[46px] w-full min-w-0 flex-1 rounded-[14px] !bg-msd-ground !shadow-none";
const FROW = "flex w-full items-center gap-3 rounded-xl border-0 px-[10px] text-left text-[15px] text-msd-ink";
const VROW = "flex w-full shrink-0 gap-3 rounded-2xl border-0 p-3 text-left";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "Yesterday" : `${days}d ago`;
}

// One choice in the filter menu: a tick when it is the one in use, the queue's
// icon, its name and how many conversations are in it.
function FilterMenuRow({ bucket, active, count, onClick }) {
  const [icon, color] = FILTER_ICON[bucket.key] || [];
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      data-testid={`desk-nav-${bucket.key}`}
      className={cx(FROW, "h-10 bg-transparent hover:bg-msd-soft", active && "font-semibold")}
    >
      <span className="flex w-4 shrink-0 text-msd-royal">{active ? <Icon name="check" size={16} stroke={2.4} /> : null}</span>
      <span className="flex shrink-0" style={{ color }}>
        {icon ? <Icon name={icon} size={18} stroke={2} /> : <bucket.icon size={18} className="text-msd-muted" />}
      </span>
      <span className="min-w-0 truncate">{bucket.label}</span>
      {bucket.connected && (
        <span className={cx("ml-auto shrink-0 text-[13px]", count > 0 ? "font-semibold text-msd-ink" : "text-msd-muted")}>{count}</span>
      )}
    </button>
  );
}

// The funnel next to the search box. It opens a short menu of the queues, like
// a spreadsheet's filter menu. The menu sits above the panel, so it is never cut
// off, scrolls when the window is short (the `msd` class brings the desk colours into the popup), and closes on a pick, a click elsewhere or Esc.
function FilterMenu({ buckets, counts, activeBucket, onSelectBucket }) {
  const [open, setOpen] = useState(false);
  const inUse = buckets.some((b) => b.key === activeBucket);
  const pick = (key) => {
    onSelectBucket(key);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filter conversations"
          aria-haspopup="menu"
          data-testid="desk-filters-toggle"
          className={cx(
            "relative flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[14px] border-0 hover:brightness-95",
            inUse ? "bg-msd-royal-soft text-msd-royal" : "bg-msd-ground text-msd-muted",
          )}
        >
          <Icon name="filter" size={19} stroke={2} />
          {inUse && <span className="absolute right-[9px] top-[9px] h-2 w-2 rounded-full bg-msd-royal ring-2 ring-msd-ground" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        role="menu"
        aria-label="Filter conversations"
        align="end"
        collisionPadding={12}
        className="msd max-h-[var(--radix-popover-content-available-height)] w-[270px] overflow-y-auto rounded-2xl border-0 bg-msd-surface p-1.5 shadow-msd-e2 ring-1 ring-msd-line"
      >
        {buckets.map((b) => (
          <FilterMenuRow
            key={b.key}
            bucket={b}
            active={b.key === activeBucket}
            count={counts[b.key] || 0}
            onClick={() => pick(b.key === activeBucket ? "all" : b.key)}
          />
        ))}
        {inUse && (
          <button
            type="button"
            onClick={() => pick("all")}
            className="mt-1 flex h-10 w-full items-center justify-center rounded-xl border-0 border-t border-msd-line bg-transparent text-[14px] font-semibold text-msd-royal hover:bg-msd-soft"
          >
            Clear filter
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

// What every list row shares: the vehicle picture, the registration with how
// long ago, then whatever lines and tags the row adds.
function RowBody({ vehicle, catalog, children }) {
  return (
    <>
      <VehicleThumb group={vehicle.row} catalog={catalog} name={vehicle.customerName} />
      <div className="flex min-w-0 grow flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <b className="truncate text-[16px] tracking-[.01em] text-msd-ink">{vehicle.vehicleReg}</b>
          <span className={cx(LBL, "shrink-0 whitespace-nowrap")}>{timeAgo(vehicle.createdAt)}</span>
        </div>
        {children}
      </div>
    </>
  );
}

const SUB = "truncate text-[14px] text-msd-muted";
const TAGS = "mt-1 flex flex-wrap gap-[6px]";

function EscalationCard({ vehicle, catalog, selected, onClick }) {
  const snippet = escalationSnippet(vehicle.escalation);
  const g = vehicle.row;
  const subtitle = [vehicle.customerName, g?._row ? `RO #${g._row}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`desk-escalation-${vehicle.id}`}
      className={cx(VROW, selected ? "bg-msd-royal-soft" : "bg-transparent hover:bg-msd-soft")}
    >
      <RowBody vehicle={vehicle} catalog={catalog}>
        {subtitle ? <div className={SUB}>{subtitle}</div> : null}
        <div className="truncate text-[13px] text-msd-ink2" title={snippet}>
          {snippet}
        </div>
        <div className={TAGS}>
          <Tag tone="red" icon="warn">Escalated</Tag>
        </div>
      </RowBody>
    </button>
  );
}

// A booking whose WhatsApp group is still being created: listed so the advisor
// can see it landed, greyed and not openable until there is a chat to open. The
// list refreshes on its own, so it turns into a normal card when the group exists.
function CreatingGroupCard({ vehicle, catalog }) {
  return (
    <div
      aria-disabled="true"
      data-testid={`desk-group-${vehicle.id}`}
      title="The WhatsApp group is being set up"
      className={cx(VROW, "cursor-not-allowed opacity-60")}
    >
      <RowBody vehicle={vehicle} catalog={catalog}>
        <div className={SUB}>{vehicle.customerName}</div>
        <div className={TAGS}>
          <span className="inline-flex h-6 items-center gap-[5px] whitespace-nowrap rounded-full bg-[rgba(120,120,128,.12)] px-[10px] text-[12px] font-medium text-msd-ink [:root[data-theme=dark]_&]:bg-white/10">
            <Loader2 size={12} className="shrink-0 animate-spin text-msd-faint" />
            Creating group…
          </span>
        </div>
      </RowBody>
    </div>
  );
}

function GroupCard({ vehicle, catalog, selected, memberSummary, onClick }) {
  const g = vehicle.row;
  const { me } = useAuth();
  const isCreating = isGroupCreating(g);
  const groupRow = isCreating ? null : vehicle.id;
  const workshopLocation = isCreating ? null : g["Workshop Location"];
  const { data: messageLog, isSuccess: messageLogLoaded } = useGroupMessageLog(groupRow);
  const { data: stakeholderData, isSuccess: stakeholdersLoaded } = useStakeholders(workshopLocation);
  const { data: memberChanges, isSuccess: memberChangesLoaded } = useMemberChanges(groupRow);
  const { data: eventsData } = useGroupEvents(groupRow);

  const liveMembers = buildMembers(
    g,
    stakeholderData?.stakeholders,
    presentNumbers(eventsData?.rows, messageLog?.rows),
    memberChanges,
    awaitingJoinFromEvents(eventsData?.rows, messageLog?.rows)
  );
  const hasLiveSummary = messageLogLoaded && memberChangesLoaded && (stakeholdersLoaded || !workshopLocation);
  const liveMemberSummary = hasLiveSummary
    ? {
      pendingMembers: liveMembers.filter((member) => member.status === "Not Joined").length,
      customerNotJoined: !customerInGroup(liveMembers),
    }
    : null;
  const displayedMemberSummary = liveMemberSummary || memberSummary;
  if (isGroupCreating(g)) return <CreatingGroupCard vehicle={vehicle} catalog={catalog} />;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`desk-group-${vehicle.id}`}
      className={cx(VROW, selected ? "bg-msd-royal-soft" : "bg-transparent hover:bg-msd-soft")}
    >
      <RowBody vehicle={vehicle} catalog={catalog}>
        <div className={SUB}>
          {vehicle.customerName}
          {g["Service Type"] ? ` · ${g["Service Type"]}` : ""}
        </div>
        {(() => {
          // A deleted group needs nothing from anyone; say so instead of
          // listing who has not joined a group that no longer exists.
          if (isWaGroupDeleted(g)) {
            return (
              <div className={TAGS}>
                <Tag tone="grey" icon="lock">Group deleted</Tag>
              </div>
            );
          }
          // Customer-not-joined leads every other pending signal on this
          // card: a group can have zero pending stakeholders while the
          // customer the group exists for still hasn't joined, and that's
          // the fact that should pull the eye first — its own capsule,
          // not folded into the generic "N members pending" count below.
          if (customerNotJoinedForCard(g, displayedMemberSummary?.customerNotJoined)) {
            return (
              <div className={TAGS}>
                <Tag tone="orange" icon="userx">Customer not joined</Tag>
              </div>
            );
          }
          // This used to render unconditionally, which was fine when the only
          // list was Pending Invites — every row in it was pending by
          // definition. In All Conversations most rows are not, and a group
          // where everyone has joined was being labelled "You haven't joined".
          const pending = pendingMemberCountForCard(g, displayedMemberSummary?.pendingMembers);
          const label =
            Number.isFinite(pending) && pending > 0
              ? `${pending} member${pending === 1 ? "" : "s"} pending`
              : me?.role === "sa" && currentUserNotJoined(g, me)
                ? "You haven't joined"
                : me?.role === "master" && serviceAdvisorNotJoined(g)
                  ? "Advisor hasn't joined"
                  : "";
          if (!label) return null;
          return (
            <div className={TAGS}>
              <Tag tone="orange" icon="cal">{label}</Tag>
            </div>
          );
        })()}
      </RowBody>
    </button>
  );
}

export default function LeftRail({
  buckets,
  counts,
  activeBucket,
  onSelectBucket,
  sectionLabel,
  vehicles,
  connected,
  listKind,
  selectedRow,
  onSelectRow,
  memberSummaries,
  search,
  onSearch,
  clientNumbers,
  numberFilter,
  onNumberFilter,
  advisors = [],
  advisorFilter = "all",
  onAdvisorFilter,
  dateRange,
  onDateRange,
  catalog,
  className,
}) {
  const primaryBuckets = buckets.filter((b) => b.primary);
  const filterBuckets = buckets.filter((b) => !b.primary);
  const chosen = filterBuckets.find((b) => b.key === activeBucket);

  return (
    <section aria-label="Conversations" className={cx(CARD, "flex min-h-0 min-w-0 flex-col overflow-hidden", className)}>
      <div className="flex flex-col gap-3 px-[18px] pb-[10px] pt-[18px]">
        <div className="flex items-center gap-2">
        <label className="flex h-[46px] min-w-0 grow items-center gap-[10px] rounded-[14px] bg-msd-ground px-[14px] text-msd-muted">
          <Icon name="search" size={18} />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search customers or vehicles"
            aria-label="Search"
            data-testid="desk-search"
            className="min-w-0 grow border-0 bg-transparent text-[15px] text-msd-ink outline-none placeholder:text-msd-muted"
          />
        </label>
        {filterBuckets.length > 0 && (
          <FilterMenu buckets={filterBuckets} counts={counts} activeBucket={activeBucket} onSelectBucket={onSelectBucket} />
        )}
        </div>
        {(clientNumbers.length > 1 || advisors.length > 0) && (
          <div className="flex gap-2">
            {clientNumbers.length > 1 && (
              <SelectBox
                value={numberFilter}
                onChange={onNumberFilter}
                label="WhatsApp number"
                testid="desk-number-filter"
                className={SELECT}
              >
                <option value="all">All WhatsApp numbers</option>
                {clientNumbers.map((n) => (
                  <option key={n} value={n}>
                    {`+${n}`}
                  </option>
                ))}
              </SelectBox>
            )}
            {advisors.length > 0 && (
              <SelectBox
                value={advisorFilter}
                onChange={onAdvisorFilter}
                label="Service advisor"
                testid="desk-advisor-filter"
                className={SELECT}
              >
                <option value="all">All advisors</option>
                {advisors.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.label}
                  </option>
                ))}
              </SelectBox>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {primaryBuckets.map((b) => (
            <Pill
              key={b.key}
              on={b.key === activeBucket}
              count={b.connected && counts[b.key] > 0 ? counts[b.key] : undefined}
              onClick={() => onSelectBucket(b.key)}
              data-testid={`desk-nav-${b.key}`}
            >
              {b.label}
            </Pill>
          ))}
          {chosen && (
            <Pill on count={counts[chosen.key] > 0 ? counts[chosen.key] : undefined} onClick={() => onSelectBucket("all")} aria-label={`${chosen.label}, tap to remove`}>
              {chosen.label}
              <Icon name="x" size={14} stroke={2.4} />
            </Pill>
          )}
          <div className="flex items-center gap-2" data-testid="desk-date-filter">
            <DateRangePicker
              range={dateRange}
              compact
              onChange={onDateRange}
              activity={NO_DATE_ACTIVITY}
              trigger={(label) => (
                <button
                  type="button"
                  data-testid="date-range-trigger"
                  className={cx(
                    "inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full border-0 bg-msd-surface px-[14px] text-[14px] font-semibold shadow-msd-e1",
                    dateRange ? "text-msd-royal" : "text-msd-ink",
                  )}
                >
                  <Icon name="cal" size={17} />
                  <span>{label}</span>
                </button>
              )}
            />
            {dateRange && (
              <LinkBtn onClick={() => onDateRange(null)} aria-label="Clear date filter" data-testid="desk-date-filter-clear">
                Clear date
              </LinkBtn>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 shrink-0 border-t border-msd-line px-[22px] pb-2 pt-[14px]">
        <p className={cx(SECL, "m-0 truncate uppercase")}>{sectionLabel}</p>
      </div>

      <div className="flex min-h-0 grow flex-col gap-1 overflow-auto px-[10px] pb-3">
        {!connected ? (
          <Empty className="m-2">Coming soon.</Empty>
        ) : vehicles.length === 0 ? (
          <Empty className="m-2">Nothing here right now.</Empty>
        ) : (
          vehicles.map((v) =>
            listKind === "escalations" ? (
              <EscalationCard key={v.id} vehicle={v} catalog={catalog} selected={selectedRow === v.id} onClick={() => onSelectRow(v.id)} />
            ) : (
              <GroupCard
                key={v.id}
                vehicle={v}
                selected={selectedRow === v.id}
                memberSummary={memberSummaries?.[v.id]}
                onClick={() => onSelectRow(v.id)}
              />
            )
          )
        )}
      </div>
    </section>
  );
}
